/* ---------------------------------------------------------------------------
   Construction Monitoring: one structure's site record.

   What was built and when, plus the three things a site produces alongside the
   work — the daily return, the incident and the filed document. The period
   report over all of them is this app's other screen
   (src/screens/worksReport.ts); this is the single structure.

   IT IS src/screens/works.ts, WHOLE, for the same reason the scheduler's screen
   is src/screens/programme.ts whole: that card is the entry list, the per-unit
   totals, the review workflow and the four dialogs, and it is already carried by
   the bridge record and drawn from by the report. Recording a pour must mean the
   same thing and be checked in one place whichever page you are on.

   THE FIELD SHELL DOES NOT COME HERE. An inspector records against the bridge
   record, which carries the same card and is three taps from the tab bar; this
   screen is a desk page in a desk application, and the field shell is Bridge
   Inspection whichever document it was opened from (see `appForShell`).
   --------------------------------------------------------------------------- */

import { h, type Child } from '../core/dom.ts';
import { type State } from '../core/store.ts';
import { emptyState } from '../components/widgets.ts';
import { appLinks } from '../components/appLinks.ts';
import { findBridge } from '../data/derive.ts';
import { documentList, incidentList, siteDiary, worksBlock, worksEntries } from './works.ts';
import { siteView } from './detail.ts';
import { itemList, testList } from './siteQuality.ts';
import { progressCharts } from './progressCharts.ts';
import { progressPhotos } from './progressPhotos.ts';
import { returnsCalendar } from './returnsCalendar.ts';
import { worksCalendar } from './worksCalendar.ts';
import { join } from '../data/recorded.ts';
import { chainage } from '../tenant/format.ts';

export function siteScreen(s: Readonly<State>): HTMLElement {
  const b = findBridge(s.selectedId);
  if (b === null) return emptyState('This register has no structures in it yet.');

  /* One list, where the record's explorer picked one: the site record's pages
     are its entries, drawn here in the content panel. */
  switch (siteView(s)) {
    case 'works':
      return s.worksLayout === 'calendar' ? sitePage(worksCalendar(s), s, 'No work has been recorded against this structure.') : sitePage(worksEntries(s), s, '');
    /* Each list draws nothing where it holds nothing, which on a page of its own
       would be a title over an empty panel — so the page says so. */
    case 'returns':
      return sitePage(
        s.returnLayout === 'calendar' ? returnsCalendar(s) : siteDiary(s, false),
        s,
        'No daily return has been recorded on this structure.',
      );
    case 'incidents':
      return sitePage(incidentList(s, false), s, 'No incident has been reported on this structure.');
    case 'documents':
      return sitePage(documentList(s, b.id, false), s, 'No document has been filed against this structure.');
    case 'tests':
      return sitePage(testList(s, false), s, 'No test result has been recorded on this structure.');
    case 'siteItems':
      return sitePage(itemList(s, false), s, 'No hold point, NCR or RFI has been raised on this structure.');
    case 'charts':
      return progressCharts(s);
    case 'progressPhotos':
      return progressPhotos(s);
  }

  return h(
    'div',
    { class: 'appscreen' },

    /* Which structure, then where else it is. In that order because the second
       line only means anything once the first has said what "this structure"
       is — and because the page head above carries the name and the district
       but not the road and the chainage, which is how somebody standing at a
       site knows it is the right one. */
    h('div', { class: 'appscreen__sub', text: join([b.id, b.route, chainage(b.km)], ' · ') }),

    appLinks(s, b),

    /* Unconditional, unlike the programme card beside it. `worksBlock` has an
       answer for every state a site record can be in — unreachable, read and
       empty, read with entries, and a device on the bundled seed — and says
       which one it is in words. A structure in service is "nothing recorded
       here", which on this screen is exactly the sentence a reader wants. */
    worksBlock(b.id, b.name, s),
  );
}

/**
 * A site-record page: the list, or — where it is empty — why, honestly: nothing
 * recorded is a fact only once the record has been read.
 */
function sitePage(list: Child, s: Readonly<State>, none: string): HTMLElement {
  if (list !== null && list !== false) return h('div', { class: 'sitelist' }, list);
  if (s.progressRead === 'unreachable') return emptyState('The construction record could not be read.');
  if (s.progressRead === 'unknown') return emptyState('The construction record has not been read yet.');
  return emptyState(none);
}
